const DEV = import.meta.env.DEV;

/**
 * Redirect to Stripe Checkout for a subscription tier.
 *
 * Calls the Supabase Edge Function `create-checkout` which creates a
 * Stripe Checkout Session server-side and returns the URL. We then
 * redirect the browser to that URL.
 *
 * @param {string} tierKey - 'enthusiast' | 'collector' | 'showroom' | 'team'
 * @param {string} userEmail - Pre-fill the checkout email
 * @param {string} userId - Supabase user ID, passed as client_reference_id
 */
export async function redirectToCheckout(tierKey, userEmail, userId) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const origin = window.location.origin;
  const successUrl = `${origin}?checkout=success&tier=${tierKey}`;
  const cancelUrl = `${origin}?checkout=cancel`;

  if (DEV) console.log('[Stripe] Creating checkout session:', { tierKey, userId, successUrl });

  const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({
      tierKey,
      userEmail,
      userId,
      successUrl,
      cancelUrl,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Checkout failed (${res.status})`;
    if (DEV) console.error('[Stripe] Edge Function error:', res.status, data);
    throw new Error(errMsg);
  }

  if (DEV) console.log('[Stripe] Checkout session created, redirecting:', data.url);

  // Redirect to Stripe Checkout
  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No checkout URL returned from server');
  }
}

/**
 * Redirect to Stripe Customer Portal for managing subscriptions/billing.
 *
 * Calls the Supabase Edge Function `create-portal-session` which looks up
 * the Stripe customer by email and creates a Billing Portal session.
 *
 * @param {string} userEmail - The user's email to find their Stripe customer
 * @param {string} userId - Supabase user ID (for logging)
 */
export async function redirectToPortal(userEmail, userId) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const returnUrl = window.location.origin;

  if (DEV) console.log('[Stripe] Creating portal session:', { userEmail, userId });

  const res = await fetch(`${supabaseUrl}/functions/v1/create-portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({ userEmail, userId, returnUrl }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Portal session failed (${res.status})`;
    if (DEV) console.error('[Stripe] Portal Edge Function error:', res.status, data);
    throw new Error(errMsg);
  }

  if (DEV) console.log('[Stripe] Portal session created, redirecting:', data.url);

  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No portal URL returned from server');
  }
}

/**
 * Get the Stripe Customer Portal URL (static fallback).
 * Pre-configured in Stripe Dashboard; stored in env var.
 */
export function getPortalUrl() {
  return import.meta.env.VITE_STRIPE_PORTAL_URL || null;
}

/* ─── Stripe Connect (Club Payouts) ─── */

/**
 * Redirect club owner to Stripe Connect onboarding.
 */
export async function redirectToConnectOnboarding(clubId, userId) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const origin = window.location.origin;

  if (DEV) console.log('[Stripe] Starting Connect onboarding:', { clubId, userId });

  const res = await fetch(`${supabaseUrl}/functions/v1/connect-onboarding`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({
      clubId,
      userId,
      returnUrl: `${origin}?connect=success&club_id=${clubId}`,
      refreshUrl: `${origin}?connect=refresh&club_id=${clubId}`,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Connect onboarding failed (${res.status})`;
    if (DEV) console.error('[Stripe] Connect onboarding error:', res.status, data);
    throw new Error(errMsg);
  }

  if (DEV) console.log('[Stripe] Connect onboarding URL, redirecting:', data.url);

  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No onboarding URL returned from server');
  }
}

/**
 * Check Connect onboarding status for a club.
 */
export async function checkConnectStatus(clubId) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const res = await fetch(`${supabaseUrl}/functions/v1/connect-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({ clubId }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Status check failed (${res.status})`;
    if (DEV) console.error('[Stripe] Connect status error:', res.status, data);
    throw new Error(errMsg);
  }

  return data;
}

/**
 * Redirect user to Stripe Checkout for a paid event.
 */
export async function redirectToEventCheckout(eventId, userId, userEmail, vehicleId, vehicleLabel) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const origin = window.location.origin;

  if (DEV) console.log('[Stripe] Creating event checkout:', { eventId, userId });

  const res = await fetch(`${supabaseUrl}/functions/v1/create-event-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({
      eventId,
      userId,
      userEmail,
      vehicleId: vehicleId || null,
      vehicleLabel: vehicleLabel || null,
      successUrl: `${origin}?event_payment=success&event_id=${eventId}`,
      cancelUrl: `${origin}?event_payment=cancel&event_id=${eventId}`,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Event checkout failed (${res.status})`;
    if (DEV) console.error('[Stripe] Event checkout error:', res.status, data);
    throw new Error(errMsg);
  }

  if (DEV) console.log('[Stripe] Event checkout session created, redirecting:', data.url);

  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No checkout URL returned from server');
  }
}

/**
 * Redirect to Stripe Checkout for a ticketed event (multi-line-item).
 * Sends ticket type + addon selections to the edge function.
 */
export async function redirectToTicketedCheckout(eventId, userId, userEmail, {
  ticketTypeId,
  vehicleId = null,
  vehicleLabel = null,
  guestCount = 0,
  guestNames = null,
  addons = [], // [{id, qty}]
} = {}) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const origin = window.location.origin;

  if (DEV) console.log('[Stripe] Creating ticketed event checkout:', { eventId, userId, ticketTypeId, addons });

  const res = await fetch(`${supabaseUrl}/functions/v1/create-event-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({
      eventId,
      userId,
      userEmail,
      ticketTypeId,
      vehicleId: vehicleId || null,
      vehicleLabel: vehicleLabel || null,
      guestCount,
      guestNames,
      addons,
      successUrl: `${origin}?event_payment=success&event_id=${eventId}`,
      cancelUrl: `${origin}?event_payment=cancel&event_id=${eventId}`,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data.error || `Event checkout failed (${res.status})`;
    if (DEV) console.error('[Stripe] Ticketed checkout error:', res.status, data);
    throw new Error(errMsg);
  }

  if (DEV) console.log('[Stripe] Ticketed checkout session created, redirecting:', data.url);

  if (data.url) {
    window.location.href = data.url;
  } else {
    throw new Error('No checkout URL returned from server');
  }
}
